import {
  SpanStatusCode,
  type Attributes,
  type AttributeValue,
  type Span,
  type Tracer,
} from "@opentelemetry/api"
import type { Cart } from "@/types"
import {
  context,
  describeError,
  emitErrorLog,
  getTracer,
  isOtelEnabled,
  normalizeError,
  trace,
} from "@/lib/otel/init"
import { setJsonAttribute } from "@/lib/otel/checkout-detail"
import { httpErrorAttributes } from "@/lib/otel/http-error"

export type CheckoutSpanAttrs = {
  merchantId?: string | null
  serviceType?: string | null
  orderTiming?: string | null
  paymentMethod?: string | null
  itemCount?: number
  cartSummary?: string
}

/** Where a checkout attempt ended; every span carries exactly one. */
export type CheckoutOutcome =
  | "confirmed"
  | "failed"
  | "duplicate_warning"
  | "pix_pending"
  | "price_review"

const failedSpans = new WeakSet<Span>()
const loggedSpans = new WeakSet<Span>()
const scalarsOf = new WeakMap<Span, Attributes>()
const detailsOf = new WeakMap<Span, Record<string, unknown>>()

/** Compact cart fingerprint for Tempo attrs (no PII beyond item names). */
export function summarizeCart(cart: Cart | null | undefined): string {
  if (!cart?.items?.length) return ""
  return cart.items
    .slice(0, 20)
    .map((ci) => {
      const opts = Object.values(ci.itemOptions ?? {})
        .map((o) => o.name)
        .filter(Boolean)
        .slice(0, 12)
        .join("+")
      return opts ? `${ci.item.name}[${opts}]` : ci.item.name
    })
    .join(" | ")
}

function newAttemptId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function startCheckoutSpan(
  name: string,
  attrs: CheckoutSpanAttrs = {},
): Span {
  const tracer: Tracer = getTracer()
  const span = tracer.startSpan(name)
  recordAttrs(span, { "checkout.attempt_id": newAttemptId() })
  setCheckoutAttrs(span, attrs)
  return span
}

/**
 * Sets scalar attributes on the span and keeps a copy, so a failure log can
 * carry everything the span knew (the API `Span` cannot be read back).
 */
export function recordAttrs(span: Span, attrs: Attributes): void {
  const defined: Attributes = {}
  for (const [key, value] of Object.entries(attrs)) {
    if (value !== undefined && value !== null) defined[key] = value as AttributeValue
  }
  span.setAttributes(defined)
  scalarsOf.set(span, { ...(scalarsOf.get(span) ?? {}), ...defined })
}

/** Stores a JSON payload on the span (chunked) and keeps the raw value for the failure log body. */
export function recordDetail(span: Span, key: string, value: unknown): void {
  setJsonAttribute(span, key, value)
  detailsOf.set(span, { ...(detailsOf.get(span) ?? {}), [key]: value })
}

export function setCheckoutAttrs(span: Span, attrs: CheckoutSpanAttrs): void {
  recordAttrs(span, {
    "checkout.merchant_id": attrs.merchantId ?? undefined,
    "checkout.service_type": attrs.serviceType ?? undefined,
    "checkout.order_timing": attrs.orderTiming ?? undefined,
    "checkout.payment_method": attrs.paymentMethod ?? undefined,
    "checkout.item_count": attrs.itemCount,
    "checkout.cart_summary": attrs.cartSummary ? attrs.cartSummary.slice(0, 2000) : undefined,
  })
}

/**
 * Runs `fn` under a checkout span. A failure reported through `markCheckoutFail`
 * or `failSpan` keeps its ERROR status; only a clean run is marked OK. An error
 * that escapes `fn` is logged as stage `unhandled`.
 */
export async function withCheckoutSpan<T>(
  name: string,
  attrs: CheckoutSpanAttrs,
  fn: (span: Span) => Promise<T>,
): Promise<T> {
  const span = startCheckoutSpan(name, attrs)
  if (!isOtelEnabled()) {
    try {
      return await fn(span)
    } finally {
      span.end()
    }
  }
  return context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const result = await fn(span)
      if (!failedSpans.has(span)) span.setStatus({ code: SpanStatusCode.OK })
      return result
    } catch (err) {
      failSpan(span, err)
      emitCheckoutFailureLog(span, "unhandled", normalizeError(err).message, null, err)
      throw err
    } finally {
      span.end()
    }
  })
}

/** Records an outcome that is neither a failure nor a confirmed order (e.g. PIX handed to the overlay). */
export function markCheckoutOutcome(span: Span, outcome: CheckoutOutcome, attrs?: Attributes): void {
  recordAttrs(span, { "checkout.outcome": outcome })
  span.addEvent(`checkout.${outcome}`, attrs)
}

/**
 * Records a thrown error on the span. Axios errors also carry the HTTP status,
 * the request path and a bounded slice of the response body. Emits no log:
 * the caller follows with `markCheckoutFail`, which names the stage.
 */
export function failSpan(span: Span, err: unknown, code?: string | null): void {
  const error = normalizeError(err)
  failedSpans.add(span)
  span.recordException(error)
  span.setStatus({ code: SpanStatusCode.ERROR, message: error.message })
  recordAttrs(span, {
    "checkout.outcome": "failed",
    "checkout.fail_code": code ?? undefined,
    "checkout.fail_message": error.message.slice(0, 500),
    ...httpErrorAttributes(err),
  })
  detailsOf.set(span, { ...(detailsOf.get(span) ?? {}), error: describeError(error) })
}

/** Marks the span failed at `stage` and emits the ERROR log that feeds the Discord alert. */
export function markCheckoutFail(
  span: Span,
  stage: string,
  message: string,
  code?: string | null,
): void {
  failedSpans.add(span)
  span.addEvent("checkout.fail", {
    "checkout.stage": stage,
    "checkout.fail_message": message.slice(0, 500),
    ...(code ? { "checkout.fail_code": code } : {}),
  })
  span.setStatus({ code: SpanStatusCode.ERROR, message })
  recordAttrs(span, {
    "checkout.stage": stage,
    "checkout.outcome": "failed",
    "checkout.fail_code": code ?? undefined,
    "checkout.fail_message": message.slice(0, 500),
  })
  emitCheckoutFailureLog(span, stage, message, code ?? null)
}

/**
 * One ERROR log per failed attempt: the span's scalar attributes become Loki
 * structured metadata (alert labels) and every JSON detail goes in the body.
 */
function emitCheckoutFailureLog(
  span: Span,
  stage: string,
  message: string,
  code: string | null,
  err?: unknown,
): void {
  if (loggedSpans.has(span)) return
  loggedSpans.add(span)
  const details = detailsOf.get(span) ?? {}
  emitErrorLog({
    name: "checkout.fail",
    message,
    attributes: {
      ...(scalarsOf.get(span) ?? {}),
      "checkout.stage": stage,
      ...(code ? { "checkout.fail_code": code } : {}),
    },
    body: {
      stage,
      code,
      ...(err ? { error: describeError(normalizeError(err)) } : {}),
      ...details,
    },
    context: trace.setSpan(context.active(), span),
  })
}

